// eslint-disable-next-line no-restricted-imports
import { ThemeProvider } from '@emotion/react';
import { sendMfaCode, verifyMfaCode } from '@iheartjane/data-access';
import { useInProgressReservations } from '@iheartjane/data-access/react-query';
import { MfaProvider } from '@iheartjane/menu';
import {
  Box,
  Flex,
  ReeferThemeProvider,
  ToastProvider,
  generateTheme,
  generateThemeCSS,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { useRuntimeConfig } from '@iheartjane/runtime-config';
import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router';

import { MenuHeaderEmbed } from '@jane/shared-ecomm/components';
import { useCurrentStoreId } from '@jane/shared-ecomm/hooks';
import {
  AuthenticationProvider,
  BrandSpecialsProvider,
  SDKProvider,
  UserPreferencesProvider,
} from '@jane/shared-ecomm/providers';
import { trackCartClick } from '@jane/shared-ecomm/tracking';
import { getStoreTheme } from '@jane/shared-ecomm/util';
import { getJwt } from '@jane/shared/auth';
import { LoadingWrapper, MfaModal } from '@jane/shared/components';
import { useJaneUser } from '@jane/shared/data-access';
/* eslint-disable-next-line no-restricted-imports */
import type { Store } from '@jane/shared/models';
import {
  PaginationCountProvider,
  getEcommPaths,
  isStore,
  parseSearch,
} from '@jane/shared/util';

import { PendingCartDrawer } from '../../components/cart/pendingCartDrawer/pendingCartDrawer';
import { useCustomerDispatch } from '../../customer/dispatch';
import {
  openCart,
  updateQueryPromoCode,
  updateTags,
} from '../../customer/redux/cart';
import { whoamiSuccess } from '../../customer/redux/customer';
import {
  selectEmbeddedApp,
  setPartner,
  verifyStore,
} from '../../customer/redux/embeddedApp';
import { selectStore } from '../../customer/redux/store';
import { useCustomerSelector } from '../../customer/selectors';
import { useAuth } from '../../hooks/useAuth/useAuth';
import { useEmbeddedOptionsListener } from '../../hooks/useEmbeddedOptionsListener';
import { useForceClientRefresh } from '../../hooks/useForceClientRefresh';
import { IdentifyCustomer } from '../../tracking/identifyCustomer';
import { SetJaneDeviceId } from '../SetJaneDeviceId';
import { ApplicationGates } from '../applicationGates/applicationGates';
import { useCartState } from '../cart/cartFacade/useCartState';
import { CheckAuthTimer } from '../checkAuthTimer';
import { ServerNotifications } from '../notifications/serverNotifications';
import { SkipLinks } from '../skipLinks';
import { StoreScripts } from './storeScripts';

export const FramelessEmbedAppContainer = () => {
  useForceClientRefresh();
  const dispatch = useCustomerDispatch();
  const location = useLocation();
  const initialStoreId = useCurrentStoreId();
  const authentication = useAuth({ initialStoreId });
  const isMobile = useMobileMediaQuery({});
  const { theme } = useEmbeddedOptionsListener();
  const { isPartnerHosted, partnerHostedPath, isBoostForAll } =
    useRuntimeConfig();

  const { isLoadingStore, store } = useCustomerSelector(selectStore);
  const { disableAuthFeatures, isVerifying, partnerId } =
    useCustomerSelector(selectEmbeddedApp);
  const { cart, cartSize } = useCartState();
  const cartProducts = cart?.products ?? [];

  const storePathProps =
    'id' in store
      ? { id: store?.id, name: store?.name, slug: store?.url_slug }
      : { id: '', name: '', slug: '' };

  const ecommPaths = getEcommPaths(
    {
      appMode: 'framelessEmbed',
      isBoostForAll,
      isPartnerHosted,
      partnerHostedPath,
      storePathProps,
    },
    location.search
  );

  const {
    data: janeUser,
    isLoading: isLoadingUser,
    isRefetching: isRefetchingUser,
  } = useJaneUser();
  const authenticated = janeUser?.user?.authenticated;

  const { data: inProgressReservations } = useInProgressReservations({
    enabled: !isRefetchingUser && authenticated,
    jwt: getJwt(),
    storeId: isStore(store) ? store.id : undefined,
  });

  // TODO: Remove this once we fully remove the customer redux state
  useEffect(() => {
    if (janeUser?.user) {
      dispatch(whoamiSuccess(janeUser?.user));
    }
  }, [janeUser?.user]);

  useEffect(() => {
    const query = parseSearch<Record<string, string | undefined>>(
      window.location.search
    );
    dispatch(updateTags(query));
    dispatch(updateQueryPromoCode(query['promo_code']));

    dispatch(setPartner({ id: initialStoreId, name: '' }));
    dispatch(
      verifyStore({
        storeId: initialStoreId!,
        options: {
          forceEmbed: !!query['embed'],
          isPartnerHosted,
          framelessEmbed: true,
        },
      })
    );
    const body = document.querySelector('body');
    if (body) {
      body.classList.add('embedded'); // set fixed modal height for embedded/whiteLabel. see layout.css
    }
  }, []);

  const onCartClick = () => {
    dispatch(openCart());
    trackCartClick();
  };

  const isLoading = isLoadingStore || isVerifying || isLoadingUser;
  /* eslint-disable-next-line @iheartjane/workspace/no-dom-globals-in-react-fc */
  const shadowHost = document.getElementById('shadow-host')?.shadowRoot;
  const drawerContainer = shadowHost?.getElementById(
    'shadow-app-sibling'
  ) as HTMLElement;

  // Inject reefer theme CSS into the shadow root instead of document.head
  // so it doesn't leak into the partner site's light DOM.
  const themeConfig = useMemo(
    () => theme || getStoreTheme(store),
    [theme, store]
  );

  useEffect(() => {
    if (!shadowHost) return;

    const STYLE_TAG_ID = 'reefer-theme-css';
    let styleTag = shadowHost.getElementById(STYLE_TAG_ID);

    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = STYLE_TAG_ID;
      shadowHost.appendChild(styleTag);
    }

    const baseTheme = generateTheme(themeConfig);
    const themeCSS = generateThemeCSS(baseTheme);
    // generateThemeCSS outputs a `:root { ... }` block. Inside a shadow DOM,
    // CSS variables must be on `:host` to scope correctly, so we swap the selector.
    // If generateThemeCSS's output format changes, this replace must be updated.
    styleTag.textContent = themeCSS.replace(':root {', ':host {');
  }, [shadowHost, themeConfig]);

  return (
    <LoadingWrapper isLoading={isLoading} variant="full-page">
      <StoreScripts storeId={partnerId} />
      <CheckAuthTimer />
      <SetJaneDeviceId />

      <IdentifyCustomer
        app="framelessEmbed"
        appStoreId={initialStoreId}
        /** @todo possible to replace with useLocation hook? */
        /* eslint-disable-next-line @iheartjane/workspace/no-dom-globals-in-react-fc */
        queryParams={parseSearch(window.location.search)}
      />
      <PaginationCountProvider>
        <ReeferThemeProvider
          mode="server"
          renderProvider={(theme, children) => (
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
          )}
          theme={themeConfig}
        >
          <ToastProvider position={isMobile ? 'top-middle' : 'top-right'}>
            <MfaProvider
              getJwt={() => getJwt()}
              sendMfaCode={sendMfaCode}
              verifyMfaCode={verifyMfaCode}
              userId={janeUser?.user?.id ?? null}
            >
              <UserPreferencesProvider>
                <AuthenticationProvider value={{ ...authentication }}>
                  <BrandSpecialsProvider
                    brandSpecialHasChanged={cart?.brand_special_has_changed}
                    cartProducts={cartProducts}
                    storeId={initialStoreId}
                  >
                    <SDKProvider>
                      <PendingCartDrawer container={drawerContainer} />
                      <MfaModal />
                      <SkipLinks />
                      {/*TODO: can ReeferThemeProvider get moved to the top level?*/}
                      <ApplicationGates />

                      <ServerNotifications />
                      <Flex pb={24} flexDirection="column" grow>
                        <MenuHeaderEmbed
                          py={24}
                          disableAuthFeatures={disableAuthFeatures}
                          inProgressReservations={
                            inProgressReservations?.length
                          }
                          onCartClick={onCartClick}
                          paths={ecommPaths}
                          productsInCart={cartSize}
                          store={store as Store}
                        />

                        <Box>
                          <LoadingWrapper isLoading={isLoading}>
                            <Outlet />
                          </LoadingWrapper>
                        </Box>
                      </Flex>
                    </SDKProvider>
                  </BrandSpecialsProvider>
                </AuthenticationProvider>
              </UserPreferencesProvider>
            </MfaProvider>
          </ToastProvider>
        </ReeferThemeProvider>
      </PaginationCountProvider>
    </LoadingWrapper>
  );
};
